<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import {
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastViewport,
} from 'reka-ui'
import { X } from 'lucide-vue-next'
import { useNotificationStore } from '@shared/stores/notifications'

// Reka UI Toast announces new toasts through a live region.
const store = useNotificationStore()
const { t, te } = useI18n()
const toneClass = {
  info: 'border-l-navy-500',
  success: 'border-l-emerald-600',
  warning: 'border-l-amber-500',
  danger: 'border-l-rose-600',
}
</script>

<template>
  <ToastProvider :label="t('toast.label')" swipe-direction="right">
    <ToastRoot
      v-for="toast in store.toasts"
      :key="toast.id"
      :duration="toast.durationMs"
      :type="toast.tone === 'danger' ? 'foreground' : 'background'"
      class="flex items-start gap-3 card border-l-4 p-4 shadow-sm"
      :class="toneClass[toast.tone]"
      :data-tone="toast.tone"
      @update:open="(open: boolean) => !open && store.dismiss(toast.id)"
    >
      <div class="flex-1">
        <ToastTitle class="text-sm font-medium text-ink">{{
          t(toast.titleKey, toast.params ?? {})
        }}</ToastTitle>
        <ToastDescription
          v-if="toast.messageKey && te(toast.messageKey)"
          class="mt-1 text-sm text-ink-secondary"
        >
          {{ t(toast.messageKey, toast.params ?? {}) }}
        </ToastDescription>
        <ToastDescription v-else-if="toast.message" class="mt-1 text-sm text-ink-secondary">
          {{ toast.message }}
        </ToastDescription>
        <p v-if="toast.correlationId" class="mt-1 text-xs text-ink-secondary">
          {{ t('errors.correlationId', { id: toast.correlationId }) }}
        </p>
      </div>
      <ToastClose
        class="rounded-full p-1 text-ink-secondary hover:bg-surface"
        :aria-label="t('common.close')"
      >
        <X class="size-4" aria-hidden="true" />
      </ToastClose>
    </ToastRoot>
    <ToastViewport
      class="fixed right-0 bottom-0 z-[60] flex w-full max-w-sm flex-col gap-2 p-4 outline-none"
    />
  </ToastProvider>
</template>
